import { EditorState, Modifier } from "draft-js";
import { Editor } from "react-draft-wysiwyg";
import { convertFromHTML } from "draft-convert";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import cn from "classnames";
import { stripUnsupportedTags } from "utils/text";
import { useCallback } from "react";

interface CustomEditorProps {
  editorState: EditorState;
  setEditorState: (editorState: EditorState) => void;
}

const CustomEditor: React.FC<CustomEditorProps> = ({
  editorState,
  setEditorState,
}) => {
  function handleReturn(e: React.KeyboardEvent) {
    return e.shiftKey; // Block Shift+Enter to create a new line
  }

  function handlePasteText(
    text: string,
    html: string,
    editorState: EditorState
  ) {
    const content = html || text;
    // wrap each line in <p> tags in case the user is pasting plain text
    // else stripUnsupportedTags will add it all into a single <p> tag
    const lines = content
      .split("\n")
      .map((line) => `<p>${line.trim()}</p>`)
      .join("");
    const sanitizedHtml = stripUnsupportedTags(lines);

    const contentBlocks = convertFromHTML(sanitizedHtml);
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();

    const newContentState = Modifier.replaceWithFragment(
      contentState,
      selectionState,
      contentBlocks.getBlockMap()
    );

    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      "insert-fragment"
    );
    setEditorState(newEditorState);
    return true; // block paste as we handled it ourselves
  }

  const handleKeyCommand = useCallback(
    (command: string): "handled" | "not-handled" => {
      // Block bold, italic, and underline commands
      if (
        command === "bold" ||
        command === "italic" ||
        command === "underline"
      ) {
        return "handled";
      }
      return "not-handled";
    },
    []
  );

  return (
    <Editor
      editorState={editorState}
      onEditorStateChange={setEditorState}
      wrapperClassName="border-none"
      editorClassName={cn("px-5", {})}
      toolbarClassName="!p-3 !mb-0"
      toolbar={{
        options: ["list"],
        list: { options: ["unordered"], unordered: { title: "Bullet List" } },
      }}
      handleReturn={handleReturn}
      handlePastedText={handlePasteText}
      handleKeyCommand={handleKeyCommand}
    />
  );
};

export default CustomEditor;
